import { useMutation } from '@apollo/client';
import { faSpinner, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faImages, faTrash, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { enableBodyScroll } from 'body-scroll-lock';
import { FieldHookConfig, useField } from 'formik';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { Range } from 'react-range';
import { ImageReference } from '../../types/MediaReference';
import { FileReference, UploadStatus, makeFileKey } from './FileUpload';
import UploadProfileMedia from '../../graphql/UploadProfileMedia.graphql';
import { useRef } from 'react';
import { ModalWindow } from '../utilities/ModalWindow';

const createImage = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', e => reject(e));
    image.src = url;
});

interface ProfilePictureUploadProps {
    maxSize: number;
    label: string;
    currentProfilePicture: FileReference | null;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

interface CropImageModalProps {
    image: FileReference;
    onUpload: (image: FileReference) => void;
    onClose: () => void;
}

const CropImageModal = ({ image, onUpload, onClose }: CropImageModalProps) => {
    const [ crop, setCrop ] = useState({ x: 0, y: 0 });
    const [ croppedAreaPixels, setCroppedAreaPixels ] = useState<Area | null>(null);
    const [ zoom, setZoom ] = useState(1);

    const onCloseHandler = () => {
        enableBodyScroll(document.body);
        onClose();
    }

    const onCropComplete = useCallback((_, croppedAreaPx) => {
        setCroppedAreaPixels(croppedAreaPx);
    }, []);

    const generateImage = async (): Promise<Blob | null> => {
        // Generate the cropped image
        const img = await createImage(image.previewUrl!);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) throw 'Unable to build canvas context.';
        if (!croppedAreaPixels) throw 'No cropped area was defined.';

        // const maxSize = Math.max(img.width, img.height);
        // const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

        canvas.width = 512;
        canvas.height = 512;

        ctx.drawImage(
            img,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            canvas.width,
            canvas.height
        );

        return new Promise(resolve => {
            canvas.toBlob(file => {
                resolve(file)
            }, 'image/jpeg')
        });
    }

    const onSave = async () => {
        const blob = await generateImage();

        if (blob) {
            (blob as any).lastModifiedDate = new Date();
            (blob as any).name = image.localFile.name;

            const newFile: File = blob as File;

            // Update the FileReference to use the new generated image
            const newFileRef: FileReference = {
                ...image,
                localFile: newFile,
                previewUrl: URL.createObjectURL(newFile)
            };

            // Call the upload callback
            onUpload(newFileRef);
            // Close the modal
            onClose();
        }
    }
    
    return (
        <ModalWindow>
            <div className='flex flex-col max-h-full relative'>
                <div className='bg-loving-500 px-4 py-4 flex items-center'>
                    <h2 className='flex-1 font-semibold text-xl ml-2 text-white'>Upload a profile picture</h2>
                    <div className='hover:bg-white hover:bg-opacity-20 transition-colors ease-in-out rounded-full cursor-pointer w-7 h-7 flex items-center justify-center' onClick={onCloseHandler}>
                        <FontAwesomeIcon fixedWidth={true} icon={faTimes} className='flex-none text-white' />
                    </div>
                </div>
                <div>
                    <div className='relative h-64 bg-gray-700'>
                        <Cropper
                            image={image.previewUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape='round'
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                    <div className='flex items-center my-6 mx-auto w-min'>
                        <label className='font-medium text-xs text-gray-700 mr-4'>Zoom</label>
                        <Range
                            step={0.1}
                            min={1}
                            max={3}
                            values={[zoom]}
                            onChange={values => setZoom(values[0])}
                            renderTrack={({ props, children }) => (
                                <div className='h-0.5 bg-gray-200 w-64' {...props}>
                                    {children}
                                </div>
                            )}
                            renderThumb={({ props }) => (
                                <div className='w-3 h-3 bg-loving-500 rounded-full' {...props} />
                            )} />
                    </div>
                </div>
                <div className='flex flex-row flex-none pb-6 px-8 space-x-2 border-gray-200 border-b md:pt-6 md:border-t md:border-b-0'>
                    <button
                        type='button'
                        className='btn btn-primary'
                        onClick={() => {
                            onSave();
                        }}>
                        Upload
                    </button>
                    <button
                        type='button'
                        className='btn'
                        onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </ModalWindow>
    );
}

export const ProfilePictureUpload = ({ maxSize, label, currentProfilePicture, setFieldValue, ...props }: ProfilePictureUploadProps & FieldHookConfig<any>) => {
    const [ field, meta ] = useField(props);
    
    const [ profilePicture, setProfilePicture ] = useState<FileReference | null>(currentProfilePicture || null);
    const [ cropTarget, setCropTarget ] = useState<FileReference | null>(null);
    const [ isCropModalOpen, setCropModalOpen ] = useState<boolean>(false);

    const [uploadFile] = useMutation<{ uploadProfileMedia: ImageReference }, { file: File, mediaType: 'BANNER' | 'PROFILE_PICTURE' }>(UploadProfileMedia);

    const onDropAcceptedHandler = (newFiles: File[]) => {
        // There should only be one file returned
        const file = newFiles[0];

        // Build a FileReference object
        const fileRef: FileReference = {
            key: makeFileKey(file.name),
            localFile: file,
            previewUrl: URL.createObjectURL(file),
            uploadStatus: UploadStatus.NotStarted
        };

        // Set the new image as the crop target
        setCropTarget(fileRef);

        // Open the crop modal window to allow the user to crop the image
        setCropModalOpen(true);
    }

    const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        maxFiles: 1,
        // File size limited to 10MB
        // All images ultimately cropped to 512x512
        maxSize: 10 * 1000 * 1000,
        accept: 'image/*',
        onDropAccepted: newFiles => onDropAcceptedHandler(newFiles)
    });

    useEffect(() => {
        setFieldValue(props.name, profilePicture);
    }, [profilePicture, props.name, setFieldValue]);

    const onUpload = async (fileRef: FileReference) => {
        // Remove the crop target now we're finished cropping the image
        setCropTarget(null);
        // Set the upload status to 'IN_PROGRESS'
        fileRef.uploadStatus = UploadStatus.InProgress;
        // Update the profile picture state
        setProfilePicture(fileRef);

        const uploadResponse = await uploadFile({
            variables: {
                file: fileRef.localFile,
                mediaType: 'PROFILE_PICTURE'
            }
        });

        setProfilePicture({
            ...fileRef,
            hostFilename: uploadResponse.data?.uploadProfileMedia.url,
            uploadStatus: UploadStatus.Completed
        });
    }

    const onReplace = () => {
        open();
    }

    const onDelete = () => {
        setFieldValue(props.name, null);
        setProfilePicture(null);
    }

    const onModalClose = () => {
        setCropModalOpen(false);
        setCropTarget(null);
    }

    const showUploadControl = !profilePicture && !cropTarget;

    return (
        <div>
            <>
            {cropTarget && (
                isCropModalOpen && <CropImageModal image={cropTarget} onUpload={onUpload} onClose={onModalClose} />
            )}
            </>
            <div className={`${showUploadControl ? 'flex' : 'hidden'} items-center justify-center w-32 h-32 rounded-full border-2 border-dashed ${isDragActive ? 'border-loving-500' : 'border-gray-300'} mt-8`} {...getRootProps()}>
                <label
                    htmlFor={`media_upload_${field.name}`}
                    className='flex items-center text-gray-700 text-sm font-medium'>
                    <FontAwesomeIcon icon={faUpload} className='text-base text-loving-500 mr-2' />
                    <span>Upload</span>
                    <input
                        type='file'
                        id={`media_upload_${field.name}`}
                        name={`media_upload_${field.name}`}
                        className='sr-only'
                        {...getInputProps()} />
                </label>
            </div>
            <div className={`${!showUploadControl ? 'flex' : 'hidden'} items-center mt-8`}>
                <div className='w-32 h-32 rounded-full overflow-hidden relative'>
                    <img src={profilePicture?.previewUrl || ''} className='object-cover w-full h-full relative' alt='' />
                    {profilePicture?.uploadStatus === UploadStatus.InProgress && (
                        <div className='absolute flex flex-col inset-0 bg-white bg-opacity-75 items-center justify-center text-gray-700'>
                            <FontAwesomeIcon icon={faSpinner} size='2x' className='fa-spin text-loving-600' />
                            <div className='text-xs font-semibold mt-3'>Uploading</div>
                        </div>
                    )}
                </div>
                {profilePicture?.uploadStatus === UploadStatus.Completed && (
                    <div className='flex flex-col ml-4'>
                        <button type='button' className='py-2 group text-left' onClick={onReplace}>
                            <FontAwesomeIcon icon={faImages} fixedWidth className='text-loving-500 mr-2' />
                            <span className='text-sm font-medium group-hover:text-loving-500'>Replace</span>
                        </button>
                        <button type='button' className='py-2 group text-left' onClick={onDelete}>
                            <FontAwesomeIcon icon={faTrash} fixedWidth className='text-loving-500 mr-2' />
                            <span className='text-sm font-medium group-hover:text-loving-500'>Delete</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
